import { Alert, Empty } from "antd";
import { showError, showSuccess } from "common/ToastNotifications";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import AddressHealthPanel from "components/organisms/AddressHealthPanel";
import GoogleMap from "components/organisms/GoogleMaps";
import isEqual from "lodash/isEqual";
import React from "react";
import { connect } from "react-redux";
import actions from "store/actions";

class LocationContainer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            error: null,
            loading: true,
            apiKeyFetched: false,
            geocoderMetadata: props.selectedDataObject?._system_geodata || null,
        };
    }

    componentDidMount() {
        this.props.getKeyByType("GOOGLE_MAP_KEY");
    }

    componentDidUpdate(prevProps) {
        const { error, getKeyByTypeResult, locationAddress, markers, selectedDataObject } = this.props;

        if (getKeyByTypeResult !== prevProps.getKeyByTypeResult) {
            this.handleKeyFetchResult(getKeyByTypeResult);
            return;
        }

        if (error !== prevProps.error) {
            this.handleApiKeyError(error);
            return;
        }

        if (this.state.apiKeyFetched && !this.state.loading) {
            const currentObjId = selectedDataObject?.dataObjectId || selectedDataObject?._id || selectedDataObject?.id;
            const prevObjId = prevProps.selectedDataObject?.dataObjectId || prevProps.selectedDataObject?._id || prevProps.selectedDataObject?.id;
            const dataChanged = locationAddress !== prevProps.locationAddress || currentObjId !== prevObjId;
            const markersChanged = !isEqual(markers, prevProps.markers);

            if (dataChanged || markersChanged) {
                this.setState({ geocoderMetadata: selectedDataObject?._system_geodata || null });
                if (this.state.error) {
                    const softErrorTypes = ["ZERO_RESULTS", "NO_RESULTS"];
                    if (softErrorTypes.includes(this.state.error.type)) {
                        // This is a soft error, so clear it and let the map update.
                        this.setState({ error: null });
                    }
                }
            }
        }
    }

    handleKeyFetchResult = (result) => {
        if (result && result.success) {
            this.setState({ apiKeyFetched: true, loading: false, error: null });
            showSuccess("Google Maps Key Found!");
        } else {
            if (result.code === "PERMISSION_DENIED") {
                this.setError("PERMISSION_ERROR", "Permission denied. Please check your API key and resource permissions.");
            } else if (result.code === "failed") {
                this.setError("NETWORK_ERROR", "Please check your internet connection or try again later.");
            } else if (result.code === "NOT_FOUND_ERROR") {
                this.setError("CONFIG_ERROR", "Google Maps API key not found. Please configure your API key in the settings.");
            } else {
                this.setError("UNKNOWN_ERROR", result.message || "An error occurred while fetching the API key.");
            }
        }
    };

    handleApiKeyError = (error) => {
        if (error) {
            const errorType = error.type || error.code || "API_ERROR";
            const errorMessage = error.message || "An error occurred with the API key service.";
            this.setError(errorType, errorMessage);
        } else {
            this.clearError();
        }
    };

    // Handle errors from GoogleMap component
    handleMapError = (type, message) => {
        this.setError(type, message);
    };

    setError = (type, message) => {
        this.setState({
            error: { type, message },
            loading: false,
            apiKeyFetched: type !== "CONFIG_ERROR",
        });
        showError(message);
    };

    clearError = () => {
        this.setState({ error: null });
    };

    handleMapSuccess = (locationData) => {
        this.setState({ error: null });

        if (locationData) {
            let qualityScore = null;
            let formattedAddress = locationData.address || null;
            let isPartial = locationData.partialMatch || false;
            // GoogleMap component passes latitude and longitude
            let coordinates =
                locationData.latitude != null && locationData.longitude != null
                    ? { lat: locationData.latitude, lng: locationData.longitude }
                    : null;

            if (locationData.isExplicitMarker) {
                qualityScore = "EXACT_COORDINATES";
            } else if (locationData.locationType) {
                qualityScore = locationData.locationType; // 'ROOFTOP', 'RANGE_INTERPOLATED', 'GEOMETRIC_CENTER', 'APPROXIMATE'
            }

            if (qualityScore) {
                const existingMetadata = this.props.selectedDataObject?._system_geodata || {};

                const geocoderMetadata = {
                    ...existingMetadata,
                    qualityScore,
                    isPartialMatch: isPartial,
                    googleFormattedAddress: formattedAddress,
                    coordinates: coordinates,
                    originalQueryAddress: this.props.locationAddress, // Track what string generated this result
                    useGoogleAddress: existingMetadata.hasOwnProperty("useGoogleAddress") ? existingMetadata.useGoogleAddress : false,
                };

                this.setState({
                    geocoderMetadata,
                });

                const { selectedDataObject, businessAreaId, schemaId, dataSetId } = this.props;
                if (selectedDataObject && businessAreaId && schemaId && dataSetId) {
                    const dataObjectId = selectedDataObject.dataObjectId || selectedDataObject._id || selectedDataObject.id;
                    const sessionCacheKey = `geocoded_${dataObjectId}`;
                    const currentAddressKey = this.props.locationAddress || "map_pin";
                    const hasBeenUpdatedInSession = sessionStorage.getItem(sessionCacheKey) === currentAddressKey;

                    if (!hasBeenUpdatedInSession) {
                        const updatedDataObject = {
                            _system_geodata: geocoderMetadata,
                            version: selectedDataObject.version,
                            validate: false, // Skip validation since we're only adding metadata and don't want to block on any required fields
                        };
                        this.props.updateDataObject(businessAreaId, schemaId, dataSetId, dataObjectId, updatedDataObject);
                        sessionStorage.setItem(sessionCacheKey, currentAddressKey);
                    }
                }
            }
        }
    };

    getAlertType = (errorType) => {
        const errorTypes = {
            CONFIG_ERROR: "error",
            PERMISSION_ERROR: "error",
            NETWORK_ERROR: "warning",
            INVALID_REQUEST: "warning",
            OVER_QUERY_LIMIT: "error",
            REQUEST_DENIED: "error",
            UNKNOWN_ERROR: "warning",
            ZERO_RESULTS: "warning",
            NO_RESULTS: "warning",
            GOOGLE_MAPS_ERROR: "error",
            MASKED_DATA: "info",
            GEOCODING_TIMEOUT: "error",
            AUTH_ERROR: "error",
            SCRIPT_LOAD_ERROR: "error",
            INVALID_API_KEY: "error",
            REFERER_NOT_ALLOWED: "error",
            QUOTA_EXCEEDED: "error",
            MAP_NOT_FOUND: "error",
            UNKNOWN_MAP_ERROR: "error",
            MAP_LOAD_TIMEOUT: "error",
        };
        return errorTypes[errorType] || "error";
    };

    handleUseGoogleAddressChange = (checked) => {
        const { geocoderMetadata } = this.state;
        if (!geocoderMetadata) return;

        const newMetadata = { ...geocoderMetadata, useGoogleAddress: checked };
        this.setState({ geocoderMetadata: newMetadata });

        const { selectedDataObject, businessAreaId, schemaId, dataSetId, locationAddress } = this.props;
        if (selectedDataObject && businessAreaId && schemaId && dataSetId) {
            const updatedDataObject = {
                _system_geodata: newMetadata,
                version: selectedDataObject.version,
                validate: false, // Skip validation since we're only adding metadata and don't want to block on any required fields
            };
            const objectId = selectedDataObject.dataObjectId || selectedDataObject._id || selectedDataObject.id;
            this.props.updateDataObject(businessAreaId, schemaId, dataSetId, objectId, updatedDataObject);

            // Update local cache so we know this obj was modified
            const sessionCacheKey = `geocoded_${objectId}`;
            sessionStorage.setItem(sessionCacheKey, locationAddress || "map_pin");
        }
    };

    renderError = () => {
        const { error } = this.state;
        if (!error) return null;

        return (
            <div className="p-4">
                <Alert
                    title={error.type.replace(/_/g, " ")}
                    description={
                        <div>
                            <p className="mb-4 content-center font-semibold">{error.message}</p>
                            <Empty description="Unable to display location map due to the error above" />
                        </div>
                    }
                    type={this.getAlertType(error.type)}
                    showIcon
                    className="mb-4"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                />
            </div>
        );
    };

    render() {
        const { loading, apiKeyFetched, error } = this.state;
        const { locationAddress, markers } = this.props;

        if (loading) {
            return <LoadingOverlay busy={true} spinner message="Please wait..." />;
        }

        if (error) {
            return this.renderError();
        }

        if (!apiKeyFetched) {
            return <LoadingOverlay busy={true} spinner message="Fetching API key..." />;
        }

        if (!this.props.apiKey?.key) {
            return <LoadingOverlay busy={true} spinner message="Loading API key..." />;
        }

        if (!locationAddress && (!markers || markers.length === 0)) {
            return (
                <div className="p-4">
                    <Alert title="No Location Data" description="No location data is currently available." type="warning" showIcon />
                </div>
            );
        }

        // Determine if we can skip geocoding by passing DB metadata directly to the map
        const { geocoderMetadata } = this.state;
        let preResolvedLocation = null;
        if (geocoderMetadata && geocoderMetadata.coordinates && geocoderMetadata.originalQueryAddress === locationAddress) {
            preResolvedLocation = {
                latitude: geocoderMetadata.coordinates.lat,
                longitude: geocoderMetadata.coordinates.lng,
                address: geocoderMetadata.googleFormattedAddress,
                locationType: geocoderMetadata.qualityScore,
                partialMatch: geocoderMetadata.isPartialMatch,
            };
        }

        return (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {this.state.geocoderMetadata && (
                    <AddressHealthPanel
                        geocoderMetadata={this.state.geocoderMetadata}
                        onUseGoogleAddressChange={this.handleUseGoogleAddressChange}
                        className="mb-2 shrink-0 shadow-sm"
                    />
                )}
                <GoogleMap
                    keyreq={this.props.apiKey.key}
                    address={locationAddress}
                    markers={markers}
                    preResolvedLocation={preResolvedLocation}
                    onError={this.handleMapError}
                    onSuccess={this.handleMapSuccess}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    apiKey: state.systemKeys.apiKey,
    error: state.systemKeys.error,
    getKeyByTypeResult: state.systemKeys.getKeyByTypeResult,
});

const mapDispatchToProps = (dispatch) => ({
    getKeyByType: (keyType) => dispatch(actions.getKeyByTypeRequest(keyType)),
    updateDataObject: (businessAreaId, schemaId, dataSetId, dataObjectId, dataObject, options) =>
        dispatch(actions.updateDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId, dataObject, options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationContainer);
